import React, { useState, useEffect } from 'react';
import { Key, Eye, EyeOff, CheckCircle, XCircle, Trash2, Zap, Loader2 } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const AdminAPIKeysGeo = () => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);

  const [googleMapsKey, setGoogleMapsKey] = useState('');
  const [openrouteKey, setOpenrouteKey] = useState('');
  const [mapboxKey, setMapboxKey] = useState('');
  const [showGoogleKey, setShowGoogleKey] = useState(false);
  const [showOpenrouteKey, setShowOpenrouteKey] = useState(false);
  const [showMapboxKey, setShowMapboxKey] = useState(false);
  const [testResults, setTestResults] = useState({});

  useEffect(() => {
    fetchKeys();
  }, []);

  const fetchKeys = async () => {
    setLoading(true);
    try {
      const response = await api.get('/pricing-admin/api-keys');
      setKeys(response.data.api_keys || []);
      
      const googleKey = response.data.api_keys.find(k => k.provider === 'google_maps');
      const openrouteK = response.data.api_keys.find(k => k.provider === 'openroute');
      const mapboxK = response.data.api_keys.find(k => k.provider === 'mapbox');
      
      if (googleKey) setGoogleMapsKey(googleKey.api_key_masked || '');
      if (openrouteK) setOpenrouteKey(openrouteK.api_key_masked || '');
      if (mapboxK) setMapboxKey(mapboxK.api_key_masked || '');
    } catch (err) {
      toast.error('Erreur chargement clés API');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveKey = async (provider, apiKey) => {
    if (!apiKey || apiKey.includes('*')) {
      toast.error('Veuillez entrer une clé API valide');
      return;
    }

    setSaving(true);
    try {
      await api.post('/pricing-admin/api-keys', {
        provider,
        api_key: apiKey,
        is_active: true
      });
      
      toast.success(`Clé API ${provider} enregistrée avec succès !`);
      fetchKeys();
    } catch (err) {
      toast.error('Erreur sauvegarde clé API');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async (provider) => {
    setTesting(provider);
    try {
      const response = await api.post('/pricing-admin/api-keys/test', { provider });
      setTestResults({
        ...testResults,
        [provider]: response.data
      });

      if (response.data.success) {
        toast.success(`${provider} connecté ! Latence: ${response.data.latency_ms}ms`);
      } else {
        toast.error(`Échec test ${provider}`);
      }
    } catch (err) {
      toast.error('Erreur test connexion');
      setTestResults({
        ...testResults,
        [provider]: { success: false, message: 'Erreur' }
      });
    } finally {
      setTesting(null);
    }
  };

  const handleDeleteKey = async (provider) => {
    if (!window.confirm(`Supprimer la clé API ${provider} ?`)) return;

    try {
      await api.delete(`/pricing-admin/api-keys/${provider}`);
      toast.success(`Clé API ${provider} supprimée`);
      fetchKeys();
      
      if (provider === 'google_maps') setGoogleMapsKey('');
      if (provider === 'openroute') setOpenrouteKey('');
      if (provider === 'mapbox') setMapboxKey('');
    } catch (err) {
      toast.error('Erreur suppression clé');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-50 p-4 sm:p-6 overflow-y-auto">
      <div className="max-w-4xl mx-auto pb-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Key className="w-8 h-8 text-emerald-600" />
            <h1 className="text-3xl font-bold text-gray-900">Clés API Géolocalisation</h1>
          </div>
          <p className="text-gray-600">Configurez les clés API pour le calcul automatique des distances</p>
        </div>

        {/* Google Maps */}
        <div className="bg-white rounded-lg shadow p-6 mb-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">Google Maps Distance Matrix API</h2>
            {testResults['google_maps'] && (
              <span className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm ${testResults['google_maps'].success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {testResults['google_maps'].success ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                {testResults['google_maps'].success ? 'Connecté' : 'Erreur'}
              </span>
            )}
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-blue-800">
              <strong>Priorité 1</strong> - API payante avec quota gratuit limité.<br />
              Obtenez une clé sur:{' '}
              <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="underline">
                Google Cloud Console
              </a>
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Clé API Google Maps</label>
            <div className="relative">
              <input
                type={showGoogleKey ? 'text' : 'password'}
                value={googleMapsKey}
                onChange={(e) => setGoogleMapsKey(e.target.value)}
                placeholder="AIzaSy..."
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md"
              />
              <button
                onClick={() => setShowGoogleKey(!showGoogleKey)}
                className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
              >
                {showGoogleKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => handleSaveKey('google_maps', googleMapsKey)}
              disabled={saving || !googleMapsKey || googleMapsKey.includes('*')}
              className="px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:bg-gray-300"
            >
              Enregistrer
            </button>
            <button
              onClick={() => handleTestConnection('google_maps')}
              disabled={testing === 'google_maps'}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-2"
            >
              {testing === 'google_maps' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
              Tester
            </button>
            {keys.find(k => k.provider === 'google_maps') && (
              <button
                onClick={() => handleDeleteKey('google_maps')}
                className="px-4 py-2 border border-red-300 text-red-600 rounded-md hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* OpenRouteService */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">OpenRouteService</h2>
            {testResults['openroute'] && (
              <span className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm ${testResults['openroute'].success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {testResults['openroute'].success ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                {testResults['openroute'].success ? 'Connecté' : 'Erreur'}
              </span>
            )}
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-blue-800">
              <strong>Fallback gratuit</strong> - Jusqu'à 2000 requêtes/jour gratuitement.<br />
              Créez un compte sur:{' '}
              <a href="https://openrouteservice.org/dev/#/signup" target="_blank" rel="noopener noreferrer" className="underline">
                OpenRouteService
              </a>
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Clé API OpenRouteService</label>
            <div className="relative">
              <input
                type={showOpenrouteKey ? 'text' : 'password'}
                value={openrouteKey}
                onChange={(e) => setOpenrouteKey(e.target.value)}
                placeholder="5b3ce..."
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md"
              />
              <button
                onClick={() => setShowOpenrouteKey(!showOpenrouteKey)}
                className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
              >
                {showOpenrouteKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => handleSaveKey('openroute', openrouteKey)}
              disabled={saving || !openrouteKey || openrouteKey.includes('*')}
              className="px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:bg-gray-300"
            >
              Enregistrer
            </button>
            <button
              onClick={() => handleTestConnection('openroute')}
              disabled={testing === 'openroute'}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-2"
            >
              {testing === 'openroute' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
              Tester
            </button>
            {keys.find(k => k.provider === 'openroute') && (
              <button
                onClick={() => handleDeleteKey('openroute')}
                className="px-4 py-2 border border-red-300 text-red-600 rounded-md hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Mapbox */}
        <div className="bg-white rounded-lg shadow p-6 mt-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">Mapbox Geocoding & Directions API</h2>
            {testResults['mapbox'] && (
              <span className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm ${testResults['mapbox'].success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {testResults['mapbox'].success ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                {testResults['mapbox'].success ? 'Connecté' : 'Erreur'}
              </span>
            )}
          </div>

          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-emerald-800">
              <strong>Recommandé pour SaaS</strong> - Geocoding + Directions avec quota généreux.<br />
              Créez un compte sur:{' '}
              <a href="https://account.mapbox.com/" target="_blank" rel="noopener noreferrer" className="underline">
                Mapbox Account
              </a>
              <br />
              <span className="text-xs">✓ Autocomplete lieux • ✓ Calcul distance/durée • ✓ Cache automatique • ✓ 100,000 requêtes/mois gratuit</span>
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Clé API Mapbox (Token)</label>
            <div className="relative">
              <input
                type={showMapboxKey ? 'text' : 'password'}
                value={mapboxKey}
                onChange={(e) => setMapboxKey(e.target.value)}
                placeholder="pk.eyJ1..."
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md"
              />
              <button
                onClick={() => setShowMapboxKey(!showMapboxKey)}
                className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
              >
                {showMapboxKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">Token public commençant par "pk."</p>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => handleSaveKey('mapbox', mapboxKey)}
              disabled={saving || !mapboxKey || mapboxKey.includes('*')}
              className="px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:bg-gray-300"
            >
              Enregistrer
            </button>
            <button
              onClick={() => handleTestConnection('mapbox')}
              disabled={testing === 'mapbox'}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-2"
            >
              {testing === 'mapbox' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
              Tester
            </button>
            {keys.find(k => k.provider === 'mapbox') && (
              <button
                onClick={() => handleDeleteKey('mapbox')}
                className="px-4 py-2 border border-red-300 text-red-600 rounded-md hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Info système */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
          <p className="text-sm text-blue-800">
            <strong>Système de fallback:</strong> Le système essaiera Mapbox (si configuré), sinon Google Maps, puis OpenRouteService en cas d'échec.
            Les distances sont mises en cache pour optimiser les performances.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminAPIKeysGeo;